import { ArdoqId, GroupingRule, ReferenceDirection } from '@ardoq/api-types';
import {
  classes,
  isTruthy,
  NonEmptyArray,
  type ContextSort,
} from '@ardoq/common-helpers';
import {
  HierarchyOfLinkedComponentsWithDirection,
  type EnhancedScopeData,
} from '@ardoq/data-model';
import {
  LI_BACKGROUND_CLASS_NAME,
  PARENT_CHILD_REFERENCE,
} from '@ardoq/global-consts';
import {
  createGraphItem,
  RawGraphNode2,
  TraversalAndGroupingResult,
  type GraphItemId,
} from '@ardoq/graph';
import type { Locale } from '@ardoq/locale';
import {
  colorsAndRepresentations,
  componentUtils,
  enhancedScopeDataOperations,
  referenceUtils,
  type FilterColorData,
} from '@ardoq/scope-data';
import {
  GET_CSS_BACKGROUND_OPTIONS,
  GLOBAL_PARENT_CHILD_COLOR,
} from '../consts';
import { getAlternateReferenceComparator } from '../getAlternateReferenceComparator';
import type {
  CleanNode,
  DependencyMapNode,
  DependencyMapReference,
  LabelsData,
} from '../types';
import { getIconColorFromScopeData } from '../utils';
import { createReferenceTypeNamesMap } from './buildNodes';
import { referenceParent } from './mapReferences';
import { calculateTextColors } from './utils';
import { type I18nLocale } from '@ardoq/i18n';
import {
  isChildOfLabel,
  isLinkToGroupingAncestor,
  isParentOfLabel,
} from '@ardoq/graph';

type BuildNodesWithHierarchyArgs = {
  graph: TraversalAndGroupingResult;
  rootItems: RawGraphNode2[];
  degreesOfParenthood: number;
  cleanNode: CleanNode;
  degreesOfChildhood?: number;
  graphAsHierarchy: Record<ArdoqId, HierarchyOfLinkedComponentsWithDirection>;
  labelsData: LabelsData;
  isNewConditionalFormatting?: boolean;
  referenceTypeNames: ReturnType<typeof createReferenceTypeNamesMap>;
  sort: ContextSort;
  locale: Locale;
  scopeData: EnhancedScopeData;
  workspaceIds: ArdoqId[];
  filterColorData: FilterColorData;
  groupBys: GroupingRule[];
  includeOnlyParentsInScope?: boolean;
  hasReferenceLabels: boolean;
  i18nLocale: I18nLocale;
  omitLinksToGroupingAncestors: boolean;
};

const NO_GROUPING_ANCESTORS: ReadonlySet<ArdoqId> = new Set();

export const buildNodesWithHierarchy = ({
  graph,
  rootItems,
  degreesOfParenthood,
  cleanNode,
  degreesOfChildhood = Infinity,
  graphAsHierarchy,
  labelsData,
  isNewConditionalFormatting,
  referenceTypeNames,
  sort,
  locale,
  scopeData,
  filterColorData,
  groupBys,
  includeOnlyParentsInScope = false,
  hasReferenceLabels,
  workspaceIds,
  i18nLocale,
  omitLinksToGroupingAncestors,
}: BuildNodesWithHierarchyArgs) => {
  const { componentMap, groupMap } = graph;

  const nodeMap = createNodeMap({
    componentMap,
    groupMap,
    rootItems,
    graphAsHierarchy,
    degreesOfParenthood,
    referenceTypeNames,
    labelsData,
    isNewConditionalFormatting,
    sort,
    locale,
    scopeData,
    workspaceIds,
    filterColorData,
    groupBys,
    includeOnlyParentsInScope,
    hasReferenceLabels,
    i18nLocale,
    omitLinksToGroupingAncestors,
  });

  const nodes = rootItems
    .map(({ id }) => nodeMap.get(id)!)
    .map(node =>
      cleanNode({
        node,
        incomingDegreesOfRelationship: 0,
        outgoingDegreesOfRelationship: 0,
        usedNodes: new Set(),
        referenceChildMaxDepth: degreesOfChildhood,
      })
    );

  return nodes;
};

type GetNodeMapArgs = {
  componentMap: Map<GraphItemId, RawGraphNode2>;
  groupMap: Map<GraphItemId, RawGraphNode2>;
  rootItems: RawGraphNode2[];
  graphAsHierarchy: Record<ArdoqId, HierarchyOfLinkedComponentsWithDirection>;
  degreesOfParenthood: number;
  referenceTypeNames: ReturnType<typeof createReferenceTypeNamesMap>;
  labelsData: LabelsData;
  isNewConditionalFormatting?: boolean;
  sort: ContextSort;
  locale: Locale;
  scopeData: EnhancedScopeData;
  workspaceIds: ArdoqId[];
  filterColorData: FilterColorData;
  groupBys: GroupingRule[];
  includeOnlyParentsInScope: boolean;
  hasReferenceLabels: boolean;
  i18nLocale: I18nLocale;
  omitLinksToGroupingAncestors: boolean;
};

const createNodeMap = ({
  componentMap,
  groupMap,
  rootItems,
  graphAsHierarchy,
  degreesOfParenthood,
  referenceTypeNames,
  labelsData,
  isNewConditionalFormatting,
  sort,
  locale,
  scopeData,
  workspaceIds,
  filterColorData,
  groupBys,
  includeOnlyParentsInScope,
  hasReferenceLabels,
  i18nLocale,
  omitLinksToGroupingAncestors,
}: GetNodeMapArgs) => {
  const nodeMap = new Map<string, DependencyMapNode>();
  const usedIds = new Set<string>();
  const context = {
    componentMap,
    degreesOfParenthood,
    graphAsHierarchy,
    groupMap,
    nodeMap,
    usedIds,
    referenceComparator: getAlternateReferenceComparator(
      referenceTypeNames,
      sort,
      locale,
      scopeData,
      workspaceIds
    ),
    isNewConditionalFormatting,
    scopeData,
    filterColorData,
    groupBys,
    includeOnlyParentsInScope,
    hasReferenceLabels,
    i18nLocale,
    omitLinksToGroupingAncestors,
  };

  rootItems.forEach(item => {
    createAndSetNodeFromGraphItem({
      rawGraphItem: item,
      context,
      labelsData,
      groupingAncestorIds: NO_GROUPING_ANCESTORS,
    });
  });
  return nodeMap;
};

type CreateAndSetNodeOrReferenceContextArgs = {
  context: {
    componentMap: Map<GraphItemId, RawGraphNode2>;
    degreesOfParenthood: number;
    graphAsHierarchy: Record<ArdoqId, HierarchyOfLinkedComponentsWithDirection>;
    groupMap: Map<GraphItemId, RawGraphNode2>;
    nodeMap: Map<GraphItemId, DependencyMapNode>;
    referenceComparator: ReturnType<typeof getAlternateReferenceComparator>;
    usedIds: Set<string>;
    isNewConditionalFormatting?: boolean;
    scopeData: EnhancedScopeData;
    filterColorData: FilterColorData;
    groupBys: GroupingRule[];
    includeOnlyParentsInScope: boolean;
    hasReferenceLabels: boolean;
    i18nLocale: I18nLocale;
    omitLinksToGroupingAncestors: boolean;
  };
};

type CreateAndSetNodeFromGraphItemArgs =
  CreateAndSetNodeOrReferenceContextArgs & {
    rawGraphItem: RawGraphNode2;
    linkedComponentParent?: HierarchyOfLinkedComponentsWithDirection;
    /**
     * We deal in the Dependency Map with two hierarchies. One given by the
     * grouping rules, and one given by the linked components. (Components of
     * the grouping hierarchy are displayed in boxes with sharp corners, the
     * ones of the linked component hierarchy are displayed in boxes with
     * rounded corners.) A root of the linked hierarchy must be a grouped
     * component to prevent visual recursion. This flag is used to prevent
     * that we rebuild the linked component hierarchy while building it.
     */
    isRecursiveCall?: boolean;
    labelsData: LabelsData;
    groupingAncestorIds: ReadonlySet<ArdoqId>;
  };

const createAndSetNodeFromGraphItem = ({
  rawGraphItem,
  linkedComponentParent,
  context,
  isRecursiveCall = false,
  labelsData,
  groupingAncestorIds,
}: CreateAndSetNodeFromGraphItemArgs) => {
  const {
    usedIds,
    graphAsHierarchy,
    referenceComparator,
    groupMap,
    componentMap,
    nodeMap,
    isNewConditionalFormatting,
  } = context;
  const graphItem = createGraphItem({ item: rawGraphItem });
  const { modelId, id } = graphItem;
  const isComponent = componentUtils.isComponent(context.scopeData, modelId);
  usedIds.add(id);
  const combinedReferences = [
    ...(!isRecursiveCall && graphAsHierarchy[modelId]
      ? getCombinedReferencesOnNode({
          linkedComponentParent: graphAsHierarchy[modelId],
          context,
          labelsData,
          groupingAncestorIds,
        })
      : []),
    ...(linkedComponentParent
      ? getCombinedReferencesOnNode({
          linkedComponentParent,
          context,
          labelsData,
          groupingAncestorIds: NO_GROUPING_ANCESTORS,
        })
      : []),
  ].sort(referenceComparator);

  const combinedReferencesChildrenIds = new Set(
    combinedReferences
      .filter(
        ({ isParentChildReference, direction }) =>
          direction === ReferenceDirection.OUTGOING && isParentChildReference
      )
      .map(({ node }) => node.modelId)
  );

  const index = graphItem.isGroup() ? graphItem.id : graphItem.modelId;
  const { mainLabel, subLabel, otherLabels } =
    labelsData.nodeLabelsData.get(index) ?? {};
  const componentColors = isComponent
    ? componentUtils.getComponentDisplayColorAsSVGAttributes(
        context.scopeData,
        context.filterColorData,
        modelId,
        { ...GET_CSS_BACKGROUND_OPTIONS, isNewConditionalFormatting }
      )
    : undefined;
  const fill = componentColors?.fill;
  const stroke = componentColors?.stroke;

  const { textColor, textModerateColor } = calculateTextColors(
    fill,
    context.isNewConditionalFormatting
  );
  const childGroupingAncestorIds = new Set([...groupingAncestorIds, modelId]);
  const node: DependencyMapNode = {
    id,
    modelId,
    isComponent,
    label: mainLabel ?? '',
    subLabel: subLabel ?? null,
    otherLabels,
    className: graphItem.getCSS(GET_CSS_BACKGROUND_OPTIONS),
    fill,
    stroke,
    textColor,
    textModerateColor,
    iconColor: isComponent
      ? getIconColorFromScopeData(
          context.scopeData,
          context.filterColorData,
          modelId,
          modelId,
          false,
          context.isNewConditionalFormatting
        )
      : '',
    representationData: isComponent
      ? colorsAndRepresentations.getComponentRepresentationData(
          context.scopeData,
          modelId
        )
      : null,
    children:
      rawGraphItem.children
        // If the child is already added as a combined parent-child reference,
        // we don't want to add it also as a child.
        ?.filter(({ id }) => !combinedReferencesChildrenIds.has(id))
        ?.map(({ id: childId }) =>
          createAndSetNodeFromGraphItem({
            rawGraphItem: groupMap.get(childId) ?? componentMap.get(childId)!,
            context,
            labelsData,
            groupingAncestorIds: childGroupingAncestorIds,
          })
        )
        .filter(isTruthy) ?? null,
    incomingReferences: null,
    outgoingReferences: null,
    combinedReferences:
      combinedReferences.length > 0 ? combinedReferences : null,
    referencesOmittedDueToRecursion: false,
    degreeOfParenthood: 0,
    degreeOfChildhood: 0,
    containsCollapsedNodes: false,
  };
  nodeMap.set(id, node);
  return node;
};

type SetCombinedReferencesOnNodeArgs =
  CreateAndSetNodeOrReferenceContextArgs & {
    linkedComponentParent: HierarchyOfLinkedComponentsWithDirection;
    labelsData: LabelsData;
    groupingAncestorIds: ReadonlySet<ArdoqId>;
  };

let hierarchyId = 0;

const getCombinedReferencesOnNode = ({
  linkedComponentParent,
  context,
  labelsData,
  groupingAncestorIds,
}: SetCombinedReferencesOnNodeArgs) => {
  const { usedIds, groupMap, degreesOfParenthood, groupBys } = context;

  return linkedComponentParent.linkedComponents
    .filter(
      linkedComponent =>
        !context.omitLinksToGroupingAncestors ||
        !isLinkToGroupingAncestor(linkedComponent, groupingAncestorIds)
    )
    .flatMap(linkedComponent => {
      const { componentId } = linkedComponent;
      const rawGraphItem = {
        id:
          // We need to ensure that we don't use a group for a linked component.
          // That would create a visual recursion. The component id could be
          // an id of a root group.
          usedIds.has(componentId) || groupMap.has(componentId)
            ? `h-${hierarchyId++}-${componentId}`
            : componentId,
        modelId: componentId,
      };
      const node = createAndSetNodeFromGraphItem({
        rawGraphItem,
        linkedComponentParent: linkedComponent,
        context,
        isRecursiveCall: true,
        labelsData,
        groupingAncestorIds: NO_GROUPING_ANCESTORS,
      });

      const referenceIds = [
        ...linkedComponent.parentConnections.map(
          ({ referenceId }) => referenceId
        ),
      ];

      if (!isListWithAtLeastOneItem(referenceIds)) {
        return null;
      }

      return linkedComponent.parentConnections.map(
        ({ referenceId, direction, isParentChildReference }) =>
          createReferenceModel({
            referenceId,
            referenceIds,
            node,
            componentId,
            degreesOfParenthood,
            direction,
            isParentChildReference,
            labelsData,
            isNewConditionalFormatting: context.isNewConditionalFormatting,
            scopeData: context.scopeData,
            filterColorData: context.filterColorData,
            groupBys,
            includeOnlyParentsInScope: context.includeOnlyParentsInScope,
            hasReferenceLabels: context.hasReferenceLabels,
            i18nLocale: context.i18nLocale,
          })
      );
    })
    .filter(isTruthy);
};

type CreateReferenceModelArgs = {
  referenceId: ArdoqId;
  referenceIds: [ArdoqId, ...ArdoqId[]];
  node: DependencyMapNode;
  componentId: ArdoqId;
  degreesOfParenthood: number;
  direction: ReferenceDirection;
  isParentChildReference: boolean;
  labelsData: LabelsData;
  isNewConditionalFormatting?: boolean;
  scopeData: EnhancedScopeData;
  filterColorData: FilterColorData;
  groupBys: GroupingRule[];
  includeOnlyParentsInScope: boolean;
  hasReferenceLabels: boolean;
  i18nLocale: I18nLocale;
};

const createReferenceModel = ({
  referenceId,
  referenceIds,
  node,
  componentId,
  degreesOfParenthood,
  direction,
  isParentChildReference,
  labelsData,
  isNewConditionalFormatting,
  scopeData,
  filterColorData,
  groupBys,
  includeOnlyParentsInScope,
  hasReferenceLabels,
  i18nLocale,
}: CreateReferenceModelArgs): DependencyMapReference => {
  const className = classes(
    referenceId
      ? referenceUtils.getReferenceCSSClassNames(
          scopeData,
          filterColorData,
          referenceId
        )
      : null,
    LI_BACKGROUND_CLASS_NAME
  );
  const { fill, stroke } =
    referenceUtils.getReferenceDisplayColorAsSVGAttributes(
      scopeData,
      filterColorData,
      referenceId,
      { useAsBackgroundStyle: true, isNewConditionalFormatting }
    );

  const { textColor, textModerateColor } = calculateTextColors(
    fill,
    isNewConditionalFormatting
  );

  const { sourceId, targetId } = getSourceAndTargetIds({
    referenceId,
    isParentChildReference,
    scopeData,
  });

  const { mainLabel, otherLabels } =
    labelsData.edgeLabelsData.get(referenceId) ?? {};

  const label = isParentChildReference
    ? getParentChildReferenceLabelWithDirection(
        direction,
        hasReferenceLabels,
        i18nLocale
      )
    : (mainLabel ?? '');

  return {
    referenceIds,
    referenceTypeId: isParentChildReference
      ? PARENT_CHILD_REFERENCE
      : (referenceUtils.getGlobalTypeId(scopeData, referenceId) ?? null),
    node,
    label,
    className,
    fill,
    stroke,
    textColor,
    textModerateColor,
    otherLabels: otherLabels,
    iconColor: isParentChildReference
      ? GLOBAL_PARENT_CHILD_COLOR
      : getIconColorFromScopeData(
          scopeData,
          filterColorData,
          componentId,
          referenceId ?? componentId,
          true,
          isNewConditionalFormatting
        ),
    // `referenceParent` really gets the parent of a referenced (or linked)
    // component.
    parent: isParentChildReference
      ? null
      : referenceParent({
          nodeId: referenceId,
          componentId,
          depth: 0,
          maxDepth: degreesOfParenthood,
          labelsData,
          isNewConditionalFormatting,
          scopeData,
          filterColorData,
          groupBys,
          includeOnlyParentsInScope,
        }),
    isCollapsedReference: false,
    direction,
    sourceId,
    targetId,
    isParentChildReference,
  };
};

type GetSourceAndTargetIdsArgs = {
  referenceId: ArdoqId;
  isParentChildReference: boolean;
  scopeData: EnhancedScopeData;
};

const getSourceAndTargetIds = ({
  referenceId,
  isParentChildReference,
  scopeData,
}: GetSourceAndTargetIdsArgs) => {
  if (isParentChildReference) {
    const [sourceId, targetId] = referenceId.split('-');
    return {
      sourceId,
      targetId,
    };
  }

  const reference = enhancedScopeDataOperations.getReference(
    scopeData,
    referenceId
  );
  return {
    sourceId: reference?.source ?? '',
    targetId: reference?.target ?? '',
  };
};

const getParentChildReferenceLabelWithDirection = (
  direction: ReferenceDirection,
  hasReferenceLabels: boolean,
  i18nLocale: I18nLocale
) =>
  !hasReferenceLabels
    ? null
    : direction === ReferenceDirection.OUTGOING
      ? isParentOfLabel(i18nLocale)
      : isChildOfLabel(i18nLocale);

const isListWithAtLeastOneItem = <T>(list: T[]): list is NonEmptyArray<T> =>
  list.length > 0;
